<template>
  <SoDialog
    v-if="supplyHistory"
    v-model="showSupplyHistory"
    card-title="Supply History"
    :loading="supplyHistory.isFetching"
  >
    <SoErrorBoundary>
      <SoSimpleTable
        v-if="supplyHistory.isFetched"
        :headers="['Date', 'Event', 'Status']"
        :items="supplyHistoryFormatted"
        show-copy-to-clipboard
      />
    </SoErrorBoundary>
  </SoDialog>
  <SoDialog v-model="showUsageChart" :max-width="900">
    <template #title>
      <div class="d-flex align-center ga-2">
        <SoIcon
          :icon="isElectricity ? 'electric' : 'gas'"
          :color="isElectricity ? 'electric' : 'gas'"
        />
        <span>{{ usageChartTitle }}</span>
        <span class="text-medium-emphasis font-weight-regular text-body-2">
          {{ meterpoint.identifier }}
        </span>
      </div>
    </template>
    <div class="text-caption text-medium-emphasis mb-2">
      Same data source as the Usage Graph in the customer's online account. For
      full information, visit the
      <a
        href="https://www.so.energy/myaccount/usage"
        target="_blank"
        rel="noopener noreferrer"
        class="text-primary"
        >usage page</a
      >.
    </div>
    <SoMixedChart
      v-if="mixedChartProps"
      :labels="mixedChartProps.labels"
      :bar-data-set="mixedChartProps.barDataSet"
      :line-data-set="mixedChartProps.lineDataSet"
      :secondary-y-axis="{ title: '£' }"
      y-axis-title="kWh"
      :tooltip-label-callback="tooltipLabel"
    />
    <div v-else class="text-medium-emphasis pa-4">
      No usage data available for this period.
    </div>
  </SoDialog>
  <SoRow
    class="border rounded bg-surface mx-0 mt-2"
    data-testid="meterpoint-widget"
  >
    <SoCol :default-width="5" class="pa-0">
      <SoWidget no-border>
        <template #text>
          <MeterpointHeader :meterpoint="meterpoint" class="mb-6" />
          <div class="meterpoints__text-layout-grid">
            <div class="text-medium-emphasis">Supply Status</div>
            <div>
              <SoStatus :status="meterpoint.status" />
            </div>
            <template v-if="meterpoint.hasSmartMeter">
              <div class="text-medium-emphasis">DCC Status</div>
              <div>
                <SoStatus :status="meterpoint.dccStatus" />
              </div>
            </template>
            <template v-if="meterpoint.hasSmartMeter">
              <div class="text-medium-emphasis">Last Smart Reading</div>
              <div>
                <SoStatus :status="meterpoint.latestSmartReadStatus" />
              </div>
            </template>
            <div class="text-medium-emphasis">Supply History</div>
            <div>
              <button
                class="text-primary text-left"
                @click="openSupplyHistoryModal(meterpoint.id)"
              >
                View
              </button>
            </div>
            <template v-if="energyUsageChecks">
              <div
                style="grid-column: 1 / -1"
                data-testid="meterpoint-energy-usage-health"
              >
                <div class="font-weight-medium text-medium-emphasis mt-2 mb-2">
                  <SoIcon icon="electric" class="mr-2" />
                  Energy Usage Data
                </div>
                <div class="d-flex flex-wrap ga-2">
                  <button
                    v-for="check in energyUsageChecks"
                    :key="check.type"
                    type="button"
                    class="energy-usage-chip"
                    :data-testid="`energy-usage-chart-button-${check.type}`"
                    :aria-label="`View ${check.title} chart`"
                    @click="openUsageChart(check.type ?? UsageType.Daily)"
                  >
                    <SoIcon icon="chart sm" class="mr-1" />
                    <SoStatus :status="check.status" />
                  </button>
                </div>
              </div>
            </template>
            <template v-else-if="meterpoint.isExport">
              <div
                class="text-medium-emphasis"
                style="grid-column: 1 / -1"
                data-testid="meterpoint-energy-usage-export-note"
              >
                <div class="font-weight-medium mt-2 mb-1">
                  <SoIcon icon="electric" class="mr-2" />
                  Energy Usage Data
                </div>
                <div class="text-caption">
                  Usage data is not available for export meters.
                </div>
              </div>
            </template>
          </div>
          <v-expand-transition>
            <div v-show="showDetails">
              <div class="meterpoints__text-layout-grid mt-5">
                <template v-if="meterpoint.profileClass">
                  <div class="text-medium-emphasis">Profile Class</div>
                  <div>{{ meterpoint.profileClass }}</div>
                </template>
                <template v-if="meterpoint.dno">
                  <div class="text-medium-emphasis">DNO</div>
                  <div>{{ meterpoint.dno }}</div>
                </template>
                <div class="text-medium-emphasis">Service Type</div>
                <div data-testid="service-type">
                  {{ meterpoint.serviceType }}
                </div>
                <div class="text-medium-emphasis">Operation</div>
                <div data-testid="operation">
                  {{ meterpoint.operationType }}
                </div>
                <template v-if="meterpoint.gsp">
                  <div class="text-medium-emphasis">GSP</div>
                  <div data-testid="gsp">
                    {{ `${meterpoint.gsp}: ${meterpoint.gspText}` }}
                  </div>
                </template>
                <template v-if="isElectricity">
                  <div class="text-medium-emphasis">MOP</div>
                  <div data-testid="mop">{{ mop }}</div>
                </template>
                <template v-else>
                  <div class="text-medium-emphasis">MAM</div>
                  <div data-testid="mam">{{ mam }}</div>
                </template>
                <template v-if="meterpoint.meters.length">
                  <template
                    v-for="register in meterpoint.meters[0].registers"
                    :key="register.id"
                  >
                    <div class="text-medium-emphasis">
                      {{ formatRateName(register.rateName, isElectricity) }}
                    </div>
                    <div data-testid="usage">{{ register.usage }}kWh</div>
                  </template>
                </template>
              </div>
            </div>
          </v-expand-transition>
        </template>
        <template #actions>
          <SoTooltip text="Coming soon...">
            <SoButton
              disabled
              title="Dataflow"
              type="text"
              @click="handleDataflowClick(meterpoint)"
            />
          </SoTooltip>
          <SoButton
            :title="showDetails ? 'Hide' : 'All Details'"
            :icon="showDetails ? 'chevron-up' : 'chevron-down'"
            type="text"
            @click="toggleAllDetails()"
          />
        </template>
      </SoWidget>
    </SoCol>
    <SoCol class="pa-4">
      <SoWidget
        v-for="meter in meterpoint.meters"
        :key="meter.id"
        filled
        class="mb-4 h-auto"
      >
        <template #text>
          <div
            class="font-weight-medium mb-4"
            data-testid="meter-serial-number"
          >
            Meter Serial Number - {{ meter.identifier }}
          </div>
          <div class="meterpoints__text-layout-grid">
            <div>Meter Type</div>
            <div class="font-weight-medium" data-testid="meter-type">
              {{ meterTypeLabel(meter) }}
            </div>
            <div>Description</div>
            <div data-testid="meter-description">
              {{ `${meter.meterType} (${meter.meterTypeDescription})` }}
            </div>
          </div>
          <div v-if="!isElectricity && meter.isIndustrialMeter" class="mt-4">
            Is an Industrial Gas Meter (Has "M0.." in serial number)
          </div>
          <div v-if="meter.isSmartMeter && meterpointConsents" class="mt-8">
            <div
              class="font-weight-medium mb-2"
              data-testid="meter-serial-number"
            >
              <SoIcon icon="update" class="mr-2" />
              Smart Reading Frequency
            </div>
            <div>
              <div class="meterpoints__text-layout-grid">
                <template v-if="isElectricity">
                  <div>Settlement data:</div>
                  <div>{{ mapSettingLabel(settlementConsent) }}</div>
                </template>

                <div>Meter data:</div>
                <div>{{ mapSettingLabel(billingConsent) }}</div>
              </div>
              <div class="mt-6">
                <SoButton
                  title="Change frequency"
                  data-testid="open-read-frequency-modal-button"
                  @click="$emit('open-change-frequency-modal', meterpoint)"
                />
              </div>
              <SoAlert
                v-if="hasAgreementWithTouProduct"
                class="mt-4"
                type="warning"
                text="This meter point is on a Time Of Use tariff. Its frequencies can only be changed to Half-Hourly."
              />
            </div>
          </div>
        </template>
      </SoWidget>
    </SoCol>
  </SoRow>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoWidget from "@/components/SoWidget.vue"
  import SoStatus from "@/components/SoStatus.vue"
  import SoRow from "@/components/SoRow.vue"
  import SoCol from "@/components/SoCol.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoErrorBoundary from "@/components/SoErrorBoundary.vue"
  import SoSimpleTable from "@/components/SoSimpleTable.vue"
  import MeterpointHeader from "@/views/customer/components/MeterpointHeader.vue"
  import { IMeterpoint } from "@/models/meterpoint/meterpoint.types"
  import { IMeter } from "@/models/meter/meter.types"
  import { RateNames } from "@/models/meterRegister/meterRegister.types"
  import { MeterpointService } from "@/services/MeterpointService"
  import { formatDate } from "@/helpers/dateHelper"
  import SoTooltip from "@/components/SoTooltip.vue"
  import SoIcon from "@/components/SoIcon.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoMixedChart from "@/components/charts/SoMixedChart.vue"
  import { IEnergyUsage } from "@/models/energyUsage/energyUsage.types"
  import { useThemeHelper } from "@/theme/themeHelper"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { IMeterpointConsent } from "@/models/meterpointConsent/meterpointConsent.types"
  import { ConsentSetting } from "@/api/backofficeRest/generated/model"
  import {
    HealthCheckInfo,
    UsageType,
  } from "@/services-handlers/getAccountHealthChecks"

  export default defineComponent({
    components: {
      SoTooltip,
      SoButton,
      SoWidget,
      SoStatus,
      SoRow,
      SoCol,
      SoDialog,
      SoErrorBoundary,
      SoSimpleTable,
      MeterpointHeader,
      SoIcon,
      SoAlert,
      SoMixedChart,
    },
    props: {
      meterpoint: {
        type: Object as PropType<IMeterpoint>,
        default: {} as IMeterpoint,
      },
      // MOP (elec) / MAM (gas) sourced from accounts/{account}/meterpoints, which
      // carries the current appointment. The meterpoint (details) prop's mop/mam
      // come from a stale endpoint and must not be shown here (SO-31001).
      mop: {
        type: String,
        default: undefined,
      },
      mam: {
        type: String,
        default: undefined,
      },
      meterpointConsents: {
        type: Object as PropType<IMeterpointConsent[]>,
        default: null,
      },
      hasAgreementWithTouProduct: {
        type: Boolean,
        required: true,
      },
      energyUsageChecks: {
        type: Array as PropType<HealthCheckInfo[]>,
        default: null,
      },
      dailyUsageData: {
        type: Array as PropType<IEnergyUsage[]>,
        default: () => [],
      },
      hhUsageData: {
        type: Array as PropType<IEnergyUsage[]>,
        default: () => [],
      },
    },
    emits: ["click", "open-change-frequency-modal"],
    data() {
      return {
        current: useCurrentAccountStore(),
        showDetails: false,
        selectedMeterpointId: null as number | null,
        showSupplyHistory: false,
        MeterpointService: MeterpointService,
        showChangeFrequencyModal: false,
        showUsageChart: false,
        selectedUsageType: UsageType.Daily as UsageType,
        UsageType,
        currentTheme: useThemeHelper(),
      }
    },
    computed: {
      isElectricity() {
        return this.meterpoint.meterpointType === "MPAN"
      },
      supplyHistory() {
        if (!this.selectedMeterpointId) return null
        return this.MeterpointService.getMeterpointSupplyHistory(
          this.selectedMeterpointId,
        )
      },
      supplyHistoryFormatted() {
        if (!this.supplyHistory) return []
        return this.supplyHistory.data.map((value) => [
          formatDate(value.date),
          value.eventType,
          value.supplyStatus,
        ])
      },
      settlementConsent() {
        return this.isElectricity
          ? this.meterpointConsents.find(
              (consent) => consent.consentDefinition === "MPAN_SETTLEMENT",
            )
          : undefined
      },
      billingConsent() {
        return this.meterpointConsents.find((consent) =>
          this.isElectricity
            ? consent.consentDefinition === "MPAN_BILLING"
            : consent.consentDefinition === "MPRN_BILLING",
        )
      },
      usageChartTitle() {
        return this.selectedUsageType === UsageType.Daily
          ? "Daily Energy Usage (last 7 days)"
          : "Half-Hourly Energy Usage (yesterday)"
      },
      mixedChartProps() {
        const usageEntries =
          this.selectedUsageType === UsageType.Daily
            ? this.dailyUsageData
            : this.hhUsageData

        if (!usageEntries?.length) return null

        const withValues = usageEntries.filter((e) => e.hasValues)
        if (!withValues.length) return null

        const firstEntry = withValues[0]
        const isHh = this.selectedUsageType === UsageType.HalfHourly

        const labels = firstEntry.values.map((v) => {
          const local = v.startDate.tz("Europe/London")
          return isHh ? local.format("HH:mm") : local.format("DD MMM")
        })

        const aggregate = (field: "kwh" | "cost") =>
          firstEntry.values.map((_, i) => {
            const raw = withValues.reduce((sum, entry) => {
              const val = entry.values[i]?.[field]
              return sum + (val ?? 0)
            }, 0)
            return field === "cost" ? raw / 100 : raw
          })

        const barColor = this.isElectricity
          ? this.currentTheme.colors.electric
          : this.currentTheme.colors.gas
        const lineColor = this.isElectricity ? "#00897B" : "#C62828"

        return {
          labels,
          barDataSet: {
            label: "Usage (kWh)",
            data: aggregate("kwh"),
            backgroundColor: barColor,
          },
          lineDataSet: {
            label: "Estimated Cost (£)",
            data: aggregate("cost"),
            borderColor: lineColor,
            backgroundColor: lineColor + "33",
            borderWidth: 2,
            pointRadius: isHh ? 0 : 3,
            fill: false,
          },
        }
      },
    },
    methods: {
      meterTypeLabel(meter: IMeter): string {
        if (meter.isUnknownMeter) return "Unknown Meter"
        return meter.isSmartMeter ? "Smart Meter" : "Traditional Meter"
      },
      mapSettingLabel(consent?: IMeterpointConsent) {
        if (!consent) return "No frequency set"
        const date = consent.startDate
          ? ` (Since ${formatDate(consent.startDate)})`
          : ""
        switch (consent.setting) {
          case ConsentSetting.HALF_HOURLY:
            return `Half-hourly${date}`
          case ConsentSetting.DAILY:
            return `Daily${date}`
          case ConsentSetting.MONTHLY:
            return `Monthly${date}`
          case ConsentSetting.NO_CONSENT:
          default:
            return "No frequency set"
        }
      },
      tooltipLabel(context: {
        dataset: { label?: string }
        parsed: { y: number | null }
      }) {
        const label = context.dataset.label ?? ""
        const value = context.parsed.y
        if (value == null) return label
        if (label.includes("Cost")) {
          return `${label}: £${value.toFixed(2)}`
        }
        return `${label}: ${value.toFixed(3)}`
      },
      openUsageChart(type: UsageType) {
        this.selectedUsageType = type
        this.showUsageChart = true
      },
      toggleAllDetails() {
        this.showDetails = !this.showDetails
      },
      openSupplyHistoryModal(id: number) {
        this.selectedMeterpointId = id
        this.showSupplyHistory = true
      },
      handleDataflowClick(meterpoint: IMeterpoint) {
        console.log(meterpoint)
      },
      formatRateName(rateName: RateNames, isElectricity: boolean) {
        if (!isElectricity) {
          return "Gas Usage"
        }

        return (rateName === "Standard" ? "Electricity" : rateName) + " Usage"
      },
    },
  })
</script>

<style scoped>
  .meterpoints__text-layout-grid {
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 16px;
  }

  .energy-usage-chip {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    border-radius: 16px;
    font-size: 0.8125rem;
    cursor: pointer;
    transition: background-color 0.15s;
  }

  .energy-usage-chip:hover {
    background-color: rgba(var(--v-theme-primary), 0.08);
  }
</style>
